import { PaginationConfig } from '@/components/features/data/DataTable'
import { ROW_ACTION_BTN_CLASS } from '@/components/features/data/DataTable/RowActionButton'
import Settings from '@/components/features/data/DataTable/Settings'
import FlyoutMenu from '@/components/layout/FlyoutMenu'
import { useInterval } from '@/hooks/useInterval'
import { ACCENT_PILL } from '@/lib/accent'
import {
  type TaskRuntime,
  describeRuntime,
  formatDuration,
  formatInputSize,
  getTaskRuntime,
} from '@/lib/taskRuntime'
import {
  NON_TERMINAL_TASK_STATUSES,
  TASK_STATUS_LABEL,
  TASK_STATUS_TONE,
} from '@/lib/taskStatusDisplay'
import { TASK_TYPE_LABEL } from '@/lib/taskTypeDisplay'
import { userDisplayName, userFor, userInitials } from '@/lib/userDisplay'
import { cn } from '@/lib/utils'
import {
  ArrowDownTrayIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/20/solid'
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  MapIcon,
  PresentationChartLineIcon,
  StopCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { useState } from 'react'

import { Avatar } from '@droidmap/frontend-ui'
import { Pagination, getPaginationSummary } from '@droidmap/frontend-ui'
import { Badge } from '@droidmap/frontend-ui'
import { Task, TaskStatus, TaskType } from '@droidmap/project-service-contract'
import { User } from '@droidmap/user-service-contract'

const STATUS_ICON: Record<
  TaskStatus,
  React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
  [TaskStatus.PENDING]: ClockIcon,
  [TaskStatus.RUNNING]: MapIcon,
  [TaskStatus.COMPLETED]: CheckIcon,
  [TaskStatus.FAILED]: XMarkIcon,
  [TaskStatus.CANCELLED]: XMarkIcon,
}

function TaskIcon({ status }: { status: TaskStatus }) {
  const tone = TASK_STATUS_TONE[status]
  const Icon = STATUS_ICON[status]
  const isRunning = status === TaskStatus.RUNNING
  return (
    <div className="relative flex-shrink-0">
      {/* Soft pulsing accent halo behind the tile — kept tight (-inset-0.5
          + blur-xs) so it hugs the tile rather than bleeding into the row. */}
      {isRunning && (
        <div
          aria-hidden
          className="bg-accent/25 pointer-events-none absolute -inset-0.5 animate-pulse rounded-md blur-xs"
        />
      )}
      <div
        className={cn(
          'relative grid h-9 w-9 place-items-center rounded-sm border',
          ACCENT_PILL[tone],
        )}
      >
        <Icon className="h-4 w-4" strokeWidth={2.5} />
        {/* SVG-traced perimeter sweep — a short accent segment travels
            along the rounded-square outline so it reads as a square loader
            rather than a circle around a square or a rotating frame. The
            dash offset is animated via tailwind's `animate-border-trace`
            keyframes; total dash length matches the rect perimeter. */}
        {isRunning && (
          <svg
            aria-hidden
            className="pointer-events-none absolute -inset-[2px] h-[calc(100%+4px)] w-[calc(100%+4px)] overflow-visible"
            viewBox="0 0 44 44"
            fill="none"
          >
            <rect
              x="2"
              y="2"
              width="40"
              height="40"
              rx="4"
              stroke="rgb(var(--accent))"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray="30 130"
              className="animate-border-trace [filter:drop-shadow(0_0_4px_rgb(var(--accent)))]"
            />
          </svg>
        )}
      </div>
    </div>
  )
}

function StatusPill({
  status,
  cancelling = false,
}: {
  status: TaskStatus
  cancelling?: boolean
}) {
  // A cancel is asynchronous — the pipeline has to terminate the job before
  // the task reaches CANCELLED — so the row says so in the meantime rather
  // than carrying on reading "Running" after the user stopped it.
  if (cancelling) {
    return (
      <Badge tone={TASK_STATUS_TONE[TaskStatus.CANCELLED]} dot pulse>
        Cancelling…
      </Badge>
    )
  }
  const isRunning = status === TaskStatus.RUNNING
  return (
    <Badge tone={TASK_STATUS_TONE[status]} dot pulse={isRunning}>
      {TASK_STATUS_LABEL[status]}
    </Badge>
  )
}

function TypeTag({ type }: { type: TaskType }) {
  return <Badge tone="cyan">{TASK_TYPE_LABEL[type]}</Badge>
}

function formatTime(d: Date | string | undefined): {
  date: string
  time: string
} {
  if (!d) return { date: '—', time: '' }
  const dt = new Date(d)
  return {
    date: dt.toLocaleDateString(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }),
    time: `${dt
      .toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
      .replace(' ', '')} ${
      Intl.DateTimeFormat().resolvedOptions().timeZone === 'UTC' ? 'UTC' : ''
    }`.trim(),
  }
}

/**
 * Runtime cell: how long the job ran, and — directly beneath it — how big the
 * input was.
 *
 * The size line is not decoration. Runtime is governed by the dataset and by
 * the matcher regime, not by a smooth multiplier (droidmap#2476 measured
 * 1635 s vs 10927 s on identical input), so a duration column on its own
 * looks erratic and invites people to read speed differences that aren't
 * there. Every duration is therefore shown against the input it processed,
 * with the derived rate in the cell's tooltip.
 */
function RuntimeCell({ runtime }: { runtime: TaskRuntime }) {
  const size = formatInputSize(runtime)
  return (
    <div className="whitespace-nowrap" title={describeRuntime(runtime)}>
      {runtime.durationMs === undefined ? (
        <span className="text-text-mute font-mono text-[12px]">—</span>
      ) : (
        <span
          className={cn(
            'flex items-center gap-1.5 font-mono text-[12px] tabular-nums',
            runtime.live ? 'text-warning' : 'text-text-dim',
          )}
        >
          {runtime.live && (
            <span
              aria-hidden
              className="bg-warning h-1.5 w-1.5 flex-shrink-0 animate-pulse rounded-full"
            />
          )}
          {formatDuration(runtime.durationMs)}
        </span>
      )}
      {size && (
        <span className="text-text-mute mt-0.5 block font-mono text-[10px] tracking-wide">
          {size}
        </span>
      )}
    </div>
  )
}

function countOutputs(task: Task): number {
  if (!task.outputAssetSetReferences) return 0
  return task.outputAssetSetReferences.reduce(
    (sum, ref) => sum + ref.viewableAssets.length,
    0,
  )
}

export type TaskSortKey = 'name' | 'status' | 'type' | 'owner' | 'created'
export type SortDirection = 'asc' | 'desc'

export interface TaskSort {
  key: TaskSortKey
  dir: SortDirection
}

interface TaskColumnDef {
  key?: TaskSortKey
  label: string
  align?: 'right'
}

const TASK_COLUMNS: TaskColumnDef[] = [
  { key: 'name', label: 'Task' },
  { key: 'status', label: 'Status' },
  // Not sortable: runtime is derived from two columns and, unlike the others,
  // has no backend sort key — sorting only the current page would silently
  // reorder a page-sized slice and read as a project-wide ranking.
  { label: 'Runtime' },
  { key: 'type', label: 'Type' },
  { key: 'owner', label: 'Owner' },
  { key: 'created', label: 'Created' },
  { label: 'Actions', align: 'right' },
]

// Seven columns, header + body share the same template so they stay aligned.
const GRID_COLS =
  'grid-cols-[minmax(0,1fr)_120px_140px_120px_170px_130px_110px]'

export interface TaskTableProps {
  tasks: Task[]
  userMap: Record<string, User>
  onView: (task: Task) => void
  onDownload?: (task: Task) => void
  onDelete: (task: Task) => void
  /** Row Edit action. When provided, the kebab menu shows Edit. */
  onEdit?: (task: Task) => void
  /**
   * Opens the task's run details — per-stage progress plus how this run
   * compares with previous runs of the same project. When provided, the kebab
   * menu shows "Run details".
   */
  onViewRunDetails?: (task: Task) => void
  /**
   * Row "Cancel job" action. When provided, the kebab menu shows it for a
   * task that is still queued or running and has a mapping job to cancel.
   * Pass it only for users who may cancel (project write).
   */
  onCancel?: (task: Task) => void
  /**
   * Tasks with a cancel in flight. Their status reads "Cancelling…" and the
   * cancel action is hidden until the task reaches a terminal status.
   */
  cancellingTaskIds?: ReadonlySet<string>
  /** Active sort + onSort handler. Headers without a `key` aren't clickable. */
  sort?: TaskSort
  onSortChange?: (sort: TaskSort) => void
  /**
   * Server-side pagination config, from either `useOffsetPaginationQuery` or
   * `useCursorPaginationQuery` — the tasks route supplies an offset one. The
   * `hasMoreBeyondKnownPages` flag on it tells the footer which of the two it
   * is; this component just forwards it.
   */
  pagination?: PaginationConfig
}

export function TaskTable({
  tasks,
  userMap,
  onView,
  onDownload,
  onDelete,
  onEdit,
  onViewRunDetails,
  onCancel,
  cancellingTaskIds,
  sort,
  onSortChange,
  pagination,
}: TaskTableProps) {
  // One clock for the whole table rather than an interval per row, and only
  // while something is actually running — a page of finished tasks re-renders
  // never, not once a second.
  const hasLiveRuntime = tasks.some(
    (task) => task.status === TaskStatus.RUNNING && !!task.startedAt,
  )
  const [now, setNow] = useState(() => Date.now())
  useInterval(() => setNow(Date.now()), hasLiveRuntime ? 1_000 : null)

  const handleSort = (key: TaskSortKey) => {
    if (!onSortChange) return
    if (sort?.key === key) {
      onSortChange({ key, dir: sort.dir === 'asc' ? 'desc' : 'asc' })
    } else {
      onSortChange({ key, dir: key === 'name' ? 'asc' : 'desc' })
    }
  }

  return (
    <div className="border-border bg-panel mb-3.5 overflow-hidden rounded-lg border">
      <div className="overflow-x-auto">
        {/* Header row mirrors the body grid template so columns align. */}
        <div
          className={cn(
            'border-border bg-bg-2 text-text-mute grid items-center gap-4 border-b px-5 py-3.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em]',
            GRID_COLS,
          )}
        >
          {TASK_COLUMNS.map((col) => {
            const isSortable = col.key !== undefined
            const isActive = sort?.key === col.key
            return (
              <div
                key={col.label}
                onClick={() => col.key && handleSort(col.key)}
                className={cn(
                  col.align === 'right' && 'text-right',
                  isSortable &&
                    'hover:text-text-dim cursor-pointer select-none',
                  isSortable && isActive && 'text-accent',
                )}
              >
                {col.label}
                {isSortable && (
                  <span
                    className={cn(
                      'ml-1.5 inline-flex align-middle',
                      !isActive && 'opacity-40',
                    )}
                  >
                    {isActive && sort?.dir === 'asc' ? (
                      <ChevronUpIcon className="h-2.5 w-2.5" />
                    ) : (
                      <ChevronDownIcon className="h-2.5 w-2.5" />
                    )}
                  </span>
                )}
              </div>
            )
          })}
        </div>

        {tasks.map((task) => {
          const owner = userFor(userMap, task.createdBy)
          const ownerName = userDisplayName(owner)
          const ownerInitials = userInitials(owner)
          const outputs = countOutputs(task)
          // `onView` navigates to the task's first output asset set, so it has
          // nothing to open until the task has produced one — output sets are
          // created when the job uploads its outputs, not when it's submitted
          // (#2469). Leave the row inert rather than dead-clickable.
          const viewable = (task.outputAssetSetReferences?.length ?? 0) > 0
          const { date, time } = formatTime(task.created)
          const isActive = NON_TERMINAL_TASK_STATUSES.includes(task.status)
          const cancelling = isActive && !!cancellingTaskIds?.has(task.id)
          const cancellable =
            !!onCancel && isActive && !cancelling && !!task.referenceId
          return (
            <div
              key={task.id}
              role={viewable ? 'button' : undefined}
              tabIndex={viewable ? 0 : undefined}
              onClick={viewable ? () => onView(task) : undefined}
              onKeyDown={
                viewable
                  ? (e) => {
                      // Only the row itself activates — keydowns bubbling up
                      // from the nested ⋮ trigger belong to that button, and
                      // cancelling them would suppress its native activation.
                      if (e.target !== e.currentTarget) return
                      if (e.key === 'Enter' || e.key === ' ') {
                        // Space scrolls the page by default; a native button
                        // would swallow that, so this div has to.
                        e.preventDefault()
                        onView(task)
                      }
                    }
                  : undefined
              }
              className={cn(
                'border-border grid items-center gap-4 border-b px-5 py-3.5 transition-colors duration-150 last:border-b-0',
                viewable && 'hover:bg-accent/[0.028] cursor-pointer',
                GRID_COLS,
              )}
            >
              <div className="flex items-center gap-3 min-w-0">
                <TaskIcon status={task.status} />
                <div className="min-w-0">
                  <span className="text-text mb-0.5 block truncate text-sm font-medium">
                    {task.name}
                  </span>
                  {outputs > 0 && (
                    <span className="text-text-mute font-mono text-[10px] tracking-wide">
                      {outputs} {outputs === 1 ? 'output' : 'outputs'}
                    </span>
                  )}
                </div>
              </div>
              <div>
                <StatusPill status={task.status} cancelling={cancelling} />
              </div>
              <RuntimeCell runtime={getTaskRuntime(task, now)} />
              <div>
                <TypeTag type={task.type} />
              </div>
              <div className="flex items-center gap-2.5 min-w-0">
                <Avatar size="md" color="violet" initials={ownerInitials} />
                <span className="text-text truncate text-[13px]">
                  {ownerName}
                </span>
              </div>
              <div className="text-text-dim whitespace-nowrap font-mono text-[12px]">
                {date}
                {time && (
                  <span className="text-text-mute mt-0.5 block text-[10px]">
                    {time}
                  </span>
                )}
              </div>
              <div
                className="flex justify-end"
                onClick={(e) => e.stopPropagation()}
              >
                <FlyoutMenu buttonClassName={ROW_ACTION_BTN_CLASS}>
                  {onViewRunDetails && (
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-border-strong text-left"
                      onClick={() => onViewRunDetails(task)}
                    >
                      <PresentationChartLineIcon className="w-4 h-4" />
                      <span className="font-medium">Run details</span>
                    </button>
                  )}
                  {onEdit && (
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-border-strong text-left"
                      onClick={() => onEdit(task)}
                    >
                      <PencilIcon className="w-4 h-4" />
                      <span className="font-medium">Edit</span>
                    </button>
                  )}
                  {viewable && (
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-border-strong text-left"
                      onClick={() => onView(task)}
                    >
                      <EyeIcon className="w-4 h-4" />
                      <span className="font-medium">View outputs</span>
                    </button>
                  )}
                  {onDownload && (
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-border-strong text-left"
                      onClick={() => onDownload(task)}
                    >
                      <ArrowDownTrayIcon className="w-4 h-4" />
                      <span className="font-medium">Download outputs</span>
                    </button>
                  )}
                  {cancellable && (
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-danger hover:bg-border-strong text-left"
                      onClick={() => onCancel(task)}
                    >
                      <StopCircleIcon className="w-4 h-4" />
                      <span className="font-medium">Cancel job</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-danger hover:bg-border-strong text-left"
                    onClick={() => onDelete(task)}
                  >
                    <TrashIcon className="w-4 h-4" />
                    <span className="font-medium">Delete</span>
                  </button>
                </FlyoutMenu>
              </div>
            </div>
          )
        })}
      </div>
      {pagination && tasks.length > 0 && (
        <div className="border-border text-text-dim flex items-center justify-between gap-3 border-t px-5 py-3.5 font-mono text-[12px] uppercase tracking-[0.06em]">
          <span>
            {getPaginationSummary({
              startRow:
                pagination.state.pageIndex * pagination.state.pageSize + 1,
              endRow:
                pagination.state.pageIndex * pagination.state.pageSize +
                tasks.length,
              total: pagination.totalCount,
              hasMoreBeyondKnownPages: pagination.hasMoreBeyondKnownPages,
            })}
          </span>
          <div className="flex items-center gap-2">
            <Settings
              pageSize={pagination.state.pageSize}
              onPageSizeChange={(pageSize) =>
                pagination.onChange({ pageIndex: 0, pageSize })
              }
            />
            <Pagination
              currentPage={pagination.state.pageIndex + 1}
              maxPage={pagination.pageCount ?? pagination.state.pageIndex + 1}
              hasMoreBeyondKnownPages={
                pagination.hasMoreBeyondKnownPages ?? false
              }
              onPageChange={(page) =>
                pagination.onChange({
                  ...pagination.state,
                  pageIndex: page - 1,
                })
              }
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default TaskTable
